import {
  Alert,
  Button,
  Center,
  Loader,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import {
  amendmentService,
  type AmendmentDraft,
} from "../../services/amendment.service";
import type {
  ReportContentVersion,
  ReportPreviewEvidence,
} from "../../services/signOff.service";
import { ReportPreviewModal } from "../workflows/ReportPreviewModal";

interface AmendmentReportPreviewModalProps {
  opened: boolean;
  caseId: string;
  draft: AmendmentDraft;
  onClose: () => void;
  onSignOut?: (evidence?: ReportPreviewEvidence) => void | Promise<void>;
  signOutPending?: boolean;
}

export function AmendmentReportPreviewModal({
  opened,
  caseId,
  draft,
  onClose,
  onSignOut,
  signOutPending,
}: AmendmentReportPreviewModalProps) {
  const [refreshFailed, setRefreshFailed] = useState(false);
  const previewQuery = useQuery({
    queryKey: [
      "case",
      caseId,
      "amendment-draft",
      draft.id,
      "sign-out-preview",
      draft.revision,
    ],
    queryFn: () =>
      amendmentService.getSignOutPreview(caseId, draft.id, draft.revision),
    enabled: opened,
  });
  const preview = previewQuery.data;
  const previewData = useMemo(
    () =>
      preview
        ? {
            ...preview.previewData,
            review: preview.review,
          }
        : undefined,
    [preview]
  );
  const clinicalSideEffectNotice = useMemo(
    () =>
      preview?.linkedStainRequests?.length
        ? `Signing this amendment will request additional stains: ${preview.linkedStainRequests
            .map(
              (request) =>
                `${
                  preview.previewData.specimens?.find(
                    (specimen) => specimen.id === request.specimenId
                  )?.number || "Specimen"
                }: ${request.stainName}`
            )
            .join("; ")}. Existing slides will be retained.`
        : undefined,
    [preview]
  );
  const handleAcknowledgeReview = useCallback(
    (contentVersion: ReportContentVersion) =>
      amendmentService.acknowledgeSignOutPreview(
        caseId,
        draft.id,
        draft.revision,
        contentVersion
      ),
    [caseId, draft.id, draft.revision]
  );
  const handleReviewContentChanged = useCallback(async () => {
    const result = await previewQuery.refetch();
    setRefreshFailed(result.isError);
  }, [previewQuery]);
  const handleRetry = useCallback(async () => {
    const result = await previewQuery.refetch();
    setRefreshFailed(result.isError);
  }, [previewQuery]);
  const handleSignOut = useCallback(
    (evidence?: ReportPreviewEvidence) => onSignOut?.(evidence),
    [onSignOut]
  );

  if (!preview || refreshFailed) {
    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title="Amendment Preview"
        size="lg"
        centered
        closeButtonProps={{ "aria-label": "Close amendment preview" }}
      >
        {previewQuery.isError || refreshFailed ? (
          <Alert
            icon={<IconAlertCircle size={18} />}
            color="red"
            title="Amendment preview unavailable"
            role="alert"
          >
            <Stack gap="sm" align="flex-start">
              <Text size="sm">
                The signed report and amendment could not be loaded together.
                Retry before signing out.
              </Text>
              <Button variant="light" color="red" onClick={handleRetry}>
                Retry Preview
              </Button>
            </Stack>
          </Alert>
        ) : (
          <Center py="xl">
            <Loader />
          </Center>
        )}
      </Modal>
    );
  }

  return (
    <ReportPreviewModal
      key={preview.review.contentVersion.fingerprint}
      opened={opened}
      onClose={onClose}
      caseId={caseId}
      title="Amendment Preview"
      clinicalSideEffectNotice={clinicalSideEffectNotice}
      reportVersion={preview.baseReportVersion}
      forceTemplateId={preview.templateId ?? undefined}
      documentViewDataOverride={preview.documentViewData}
      previewDataOverride={previewData}
      pathologistCommentsOverride={preview.pathologistComments}
      liveData={{
        frozenInterpretation: preview.previewData.frozenInterpretation,
      }}
      reviewRequired={preview.review.reviewRequired}
      onAcknowledgeReview={handleAcknowledgeReview}
      onReviewContentChanged={handleReviewContentChanged}
      onSignOut={onSignOut ? handleSignOut : undefined}
      signOutPending={signOutPending}
      allowSignOutWhenFinalized
      simulateMainSignature={false}
      showPrintAction={false}
      signOutLabel={
        preview.review.reviewRequired
          ? "Sign Out Reviewed Amendment"
          : "Sign Out Amendment"
      }
      readOnly={!onSignOut}
    />
  );
}
